// ─── Stundensatz-Kalkulation (Mittellohn) ────────────────────────────────────
// Reine Funktion: Personal, Gemeinkosten und Wagnis & Gewinn rein, Verrechnungs-
// sätze je Lohngruppe + Rechenweg raus. Die KI füllt höchstens Eingaben vor.
//
// Schema (Zuschlagskalkulation im Handwerk):
//   produktive Stunden = (Soll-Arbeitstage − Ausfalltage) × Tagesstunden × verrechenbarer Anteil
//   Lohnkosten je h    = Personalkosten (Fertigungsanteil) ÷ produktive Stunden
//   Gemeinkosten je h  = (Gemeinkosten + Personalkosten Verwaltungsanteil − anders gedeckt) ÷ Σ produktive Stunden
//   Selbstkosten je h  = Lohnkosten je h + Gemeinkosten je h
//   Verrechnungssatz   = Selbstkosten je h × (1 + Wagnis & Gewinn)
// Urlaub, Feiertage und Krankheit stecken damit automatisch im Satz: die Kosten
// fürs ganze Jahr werden nur auf die Stunden verteilt, die man berechnen kann.
//
// Ergebnisse sind Zahlen (€ auf 2, Stunden auf 2 Stellen) — intern wird exakt
// gerechnet und erst am Ende gerundet.

import { d, summe } from './dezimal.js';
import { arbeitstage, iso } from './zeit.js';

export const PERSONAL_ROLLEN = ['produktiv', 'gemischt', 'verwaltung'];

const leer = (x) => x === null || x === undefined || x === '';
const z2 = (x) => d(x).alsZahl(2);

/** Soll-Arbeitstage eines Jahres: Arbeitstage des Modells ohne gesetzliche Feiertage. */
export function sollArbeitstage(jahr, { bundesland = 'SH', wochentage = [1, 2, 3, 4, 5] } = {}) {
  return arbeitstage(iso(jahr, 1, 1), iso(jahr, 12, 31), { bundesland, wochentage }).summe;
}

/**
 * Produktive (verrechenbare) Stunden einer Person im Jahr.
 * person: { wochenstunden, wochentage, urlaubstage, krankheitstage, schultage,
 *           weiterbildungstage, sonstige_ausfalltage, produktiv_anteil (0–1), rolle }
 */
export function produktiveStunden(person, { jahr, bundesland = 'SH' }) {
  const hinweise = [];
  const name = person.name || 'Person';
  const wochentage = person.wochentage?.length ? person.wochentage : [1, 2, 3, 4, 5];
  const soll = d(sollArbeitstage(jahr, { bundesland, wochentage }));

  const ausfallFelder = [
    ['urlaubstage', 'Urlaub'],
    ['krankheitstage', 'Krankheit'],
    ['schultage', 'Berufsschule'],
    ['weiterbildungstage', 'Weiterbildung'],
    ['sonstige_ausfalltage', 'Sonstige Ausfälle'],
  ];
  const ausfall = ausfallFelder.map(([feld, label]) => ({ label, tage: d(person[feld] || 0) }));
  if (leer(person.krankheitstage) && person.rolle !== 'verwaltung') {
    hinweise.push(`${name}: Krankheitstage fehlen — mit 0 gerechnet, der Satz fällt dadurch zu niedrig aus.`);
  }
  const ausfallSumme = summe(ausfall.map((a) => a.tage));
  let anwesenheitstage = soll.minus(ausfallSumme);
  if (anwesenheitstage.istNegativ()) {
    hinweise.push(`${name}: mehr Ausfalltage als Arbeitstage — Anwesenheit auf 0 gesetzt.`);
    anwesenheitstage = d(0);
  }
  const tagesstunden = d(person.wochenstunden || 0).durch(wochentage.length);
  const anwesenheitsstunden = anwesenheitstage.mal(tagesstunden);

  let anteil;
  if (person.rolle === 'verwaltung') {
    anteil = d(0);
  } else if (leer(person.produktiv_anteil)) {
    anteil = d(1);
    hinweise.push(`${name}: verrechenbarer Anteil fehlt — mit 100 % gerechnet. Rüst-, Werkstatt- und Aufräumzeit fehlen dann im Satz.`);
  } else {
    anteil = d(person.produktiv_anteil);
    if (anteil.istNegativ() || anteil.groesserAls(1)) {
      hinweise.push(`${name}: verrechenbarer Anteil muss zwischen 0 und 100 % liegen.`);
      anteil = anteil.istNegativ() ? d(0) : d(1);
    }
  }
  const produktiv = anwesenheitsstunden.mal(anteil);

  return {
    soll_tage: soll,
    ausfall,
    ausfall_tage: ausfallSumme,
    anwesenheitstage,
    tagesstunden,
    anwesenheitsstunden,
    produktiv_anteil: anteil,
    produktive_stunden: produktiv,
    hinweise,
  };
}

/**
 * Personalkosten einer Person im Jahr (Arbeitgeber-Gesamtkosten).
 * Vorrang: arbeitgeberkosten_jahr (z. B. aus dem Lohnjournal) → Unternehmerlohn
 * (Inhaber) → Aufbau aus Brutto + Sonderzahlungen + Arbeitgeberanteil %.
 */
export function personalkosten(person) {
  const hinweise = [];
  const name = person.name || 'Person';
  const rechenweg = [];

  if (!leer(person.arbeitgeberkosten_jahr)) {
    const k = d(person.arbeitgeberkosten_jahr);
    rechenweg.push({ schritt: 'Arbeitgeber-Gesamtkosten (angegeben)', wert: z2(k) });
    return { kosten: k, art: 'gesamt', rechenweg, hinweise };
  }
  if (person.ist_inhaber) {
    if (leer(person.unternehmerlohn_jahr)) {
      hinweise.push(`${name}: kalkulatorischer Unternehmerlohn fehlt — ohne ihn ist der Stundensatz zu niedrig.`);
      return { kosten: d(0), art: 'unternehmerlohn', rechenweg, hinweise };
    }
    const k = d(person.unternehmerlohn_jahr);
    rechenweg.push({ schritt: 'Kalkulatorischer Unternehmerlohn', wert: z2(k) });
    return { kosten: k, art: 'unternehmerlohn', rechenweg, hinweise };
  }

  let brutto;
  if (!leer(person.bruttolohn_jahr)) {
    brutto = d(person.bruttolohn_jahr);
    rechenweg.push({ schritt: 'Bruttolohn im Jahr', wert: z2(brutto) });
  } else if (!leer(person.monatsgehalt)) {
    brutto = d(person.monatsgehalt).mal(12);
    rechenweg.push({ schritt: `Bruttolohn = ${d(person.monatsgehalt).alsText(2)} € × 12 Monate`, wert: z2(brutto) });
  } else if (!leer(person.stundenlohn)) {
    // Bezahlt werden alle Wochenstunden des Jahres — auch Urlaub, Feiertage, Krankheit
    brutto = d(person.stundenlohn).mal(d(person.wochenstunden || 0)).mal(52);
    rechenweg.push({ schritt: `Bruttolohn = ${d(person.stundenlohn).alsText(2)} €/h × ${d(person.wochenstunden || 0).alsText(2)} h × 52 Wochen`, wert: z2(brutto) });
  } else {
    hinweise.push(`${name}: kein Lohn angegeben.`);
    return { kosten: d(0), art: 'aufbau', rechenweg, hinweise };
  }

  const sonder = d(person.sonderzahlungen_jahr || 0);
  if (!sonder.istNull()) rechenweg.push({ schritt: 'Sonderzahlungen (Urlaubs-/Weihnachtsgeld, VL)', wert: z2(sonder) });
  const basis = brutto.plus(sonder);

  if (leer(person.ag_anteil_prozent)) {
    hinweise.push(`${name}: Arbeitgeberanteil (Sozialversicherung, Umlagen, BG) fehlt — nur der Bruttolohn ist gerechnet.`);
  }
  const ag = basis.mal(d(person.ag_anteil_prozent || 0)).durch(100);
  if (!ag.istNull()) rechenweg.push({ schritt: `Arbeitgeberanteil ${d(person.ag_anteil_prozent).alsText(2)} %`, wert: z2(ag) });
  const k = basis.plus(ag);
  rechenweg.push({ schritt: 'Personalkosten im Jahr', wert: z2(k) });
  return { kosten: k, art: 'aufbau', rechenweg, hinweise };
}

function ampel(aktuell, selbstkosten, verrechnungssatz) {
  if (leer(aktuell)) return null;
  const a = d(aktuell);
  if (!a.kleinerAls(verrechnungssatz.runde(2))) return 'gruen';
  if (!a.kleinerAls(selbstkosten.runde(2))) return 'gelb';
  return 'rot';
}

/**
 * @param eingabe {
 *   jahr, bundesland,
 *   personen: [{ id, name, rolle: 'produktiv'|'gemischt'|'verwaltung', ist_inhaber,
 *                lohngruppe_id, … siehe produktiveStunden / personalkosten }],
 *   lohngruppen: [{ id, name }],
 *   gemeinkosten: [{ bezeichnung, betrag_jahr, quelle }],
 *   abzuege: [{ bezeichnung, betrag_jahr }],          // anders gedeckt (Maschinensätze …)
 *   wagnis_gewinn_prozent,
 *   aktuelle_saetze: { [lohngruppe_id]: verrechnungssatz }   // zum Vergleich
 * }
 */
export function stundensatzKalkulation(eingabe) {
  const jahr = Number(eingabe.jahr);
  const bundesland = eingabe.bundesland || 'SH';
  const fehler = [];
  const hinweise = [];
  const rechenweg = [];

  if (!Number.isInteger(jahr)) fehler.push('Das Jahr fehlt.');
  const personenEin = eingabe.personen || [];
  if (fehler.length) return { fehler, hinweise, rechenweg };

  // ── 1. Personen: produktive Stunden und Kosten ──
  const personen = personenEin.map((p) => {
    const rolle = PERSONAL_ROLLEN.includes(p.rolle) ? p.rolle : 'produktiv';
    const person = { ...p, rolle };
    const st = produktiveStunden(person, { jahr, bundesland });
    const ko = personalkosten(person);
    hinweise.push(...st.hinweise, ...ko.hinweise);
    // Fertigungsanteil der Kosten: produktiv ganz, gemischt nach Anteil, Verwaltung gar nicht
    const fertigungsanteil = rolle === 'produktiv' ? d(1) : st.produktiv_anteil;
    const kostenFertigung = ko.kosten.mal(fertigungsanteil);
    const kostenGemein = ko.kosten.minus(kostenFertigung);
    const lohnJeStunde = st.produktive_stunden.istNull() ? null : kostenFertigung.durch(st.produktive_stunden);
    return {
      id: p.id ?? null,
      name: p.name || '',
      rolle,
      ist_inhaber: !!p.ist_inhaber,
      lohngruppe_id: p.lohngruppe_id ?? null,
      _st: st,
      _kosten: ko.kosten,
      _fertigung: kostenFertigung,
      _gemein: kostenGemein,
      _lohnJeStunde: lohnJeStunde,
      rechenweg: [
        { schritt: `Soll-Arbeitstage ${jahr} (${bundesland}, ohne Feiertage)`, wert: z2(st.soll_tage) },
        ...st.ausfall.filter((a) => !a.tage.istNull()).map((a) => ({ schritt: `− ${a.label}`, wert: z2(a.tage) })),
        { schritt: `Anwesenheit = ${st.anwesenheitstage.alsText(2)} Tage × ${st.tagesstunden.alsText(2)} h`, wert: z2(st.anwesenheitsstunden) },
        { schritt: `Davon verrechenbar ${st.produktiv_anteil.mal(100).alsText(2)} %`, wert: z2(st.produktive_stunden) },
        ...ko.rechenweg,
        ...(rolle === 'gemischt' ? [{ schritt: `Davon Fertigung ${st.produktiv_anteil.mal(100).alsText(2)} %, Rest in die Gemeinkosten`, wert: z2(kostenFertigung) }] : []),
        ...(rolle === 'verwaltung' ? [{ schritt: 'Ganz in die Gemeinkosten (nicht verrechenbar)', wert: z2(kostenGemein) }] : []),
        ...(lohnJeStunde ? [{ schritt: 'Lohnkosten je verrechenbare Stunde', wert: z2(lohnJeStunde) }] : []),
      ],
    };
  });

  const produktiveSumme = summe(personen.map((p) => p._st.produktive_stunden));
  const fertigungslohn = summe(personen.map((p) => p._fertigung));
  if (produktiveSumme.istNull()) {
    fehler.push('Niemand hat verrechenbare Stunden — ohne produktive Stunden gibt es keinen Stundensatz.');
  }
  if (personen.some((p) => p.ist_inhaber) === false) {
    hinweise.push('Kein Inhaber erfasst. Arbeitet der Inhaber mit, gehören seine Stunden und ein kalkulatorischer Unternehmerlohn hinein.');
  }

  // ── 2. Gemeinkosten ──
  const posten = (eingabe.gemeinkosten || []).map((g) => ({
    bezeichnung: g.bezeichnung || 'Gemeinkosten', betrag: d(g.betrag_jahr || 0), quelle: g.quelle || null,
  }));
  const abzuege = (eingabe.abzuege || []).map((a) => ({ bezeichnung: a.bezeichnung || 'Abzug', betrag: d(a.betrag_jahr || 0) }));
  for (const x of [...posten, ...abzuege]) {
    if (x.betrag.istNegativ()) fehler.push(`${x.bezeichnung}: Betrag darf nicht negativ sein.`);
  }
  const gkPosten = summe(posten.map((p) => p.betrag));
  const gkPersonal = summe(personen.map((p) => p._gemein));
  const gkAbzug = summe(abzuege.map((a) => a.betrag));
  let gkSumme = gkPosten.plus(gkPersonal).minus(gkAbzug);
  if (gkSumme.istNegativ()) {
    fehler.push('Die Abzüge sind größer als die Gemeinkosten.');
    gkSumme = d(0);
  }
  if (posten.length === 0) hinweise.push('Keine Gemeinkosten erfasst (Werkstatt, Fahrzeuge, Versicherungen …) — der Satz deckt dann nur die Löhne.');
  if (fehler.length) return { fehler, hinweise, rechenweg };

  const gkJeStunde = gkSumme.durch(produktiveSumme);
  const gkZuschlag = fertigungslohn.istNull() ? null : gkSumme.durch(fertigungslohn).mal(100);

  // ── 3. Wagnis & Gewinn ──
  if (leer(eingabe.wagnis_gewinn_prozent)) hinweise.push('Wagnis & Gewinn fehlt — der Satz deckt nur die Kosten.');
  const wg = d(eingabe.wagnis_gewinn_prozent || 0);
  const faktor = d(1).plus(wg.durch(100));

  // ── 4. Je Lohngruppe und als Mittellohn ──
  const namen = new Map((eingabe.lohngruppen || []).map((g) => [g.id, g.name]));
  const aktuell = eingabe.aktuelle_saetze || {};
  const gruppenIds = [...new Set(personen.filter((p) => !p._st.produktive_stunden.istNull()).map((p) => p.lohngruppe_id))];
  const lohngruppen = gruppenIds.map((id) => {
    const mitglieder = personen.filter((p) => p.lohngruppe_id === id && !p._st.produktive_stunden.istNull());
    const stunden = summe(mitglieder.map((p) => p._st.produktive_stunden));
    const lohn = summe(mitglieder.map((p) => p._fertigung)).durch(stunden);
    const selbst = lohn.plus(gkJeStunde);
    const vs = selbst.mal(faktor);
    const jetzt = id && !leer(aktuell[id]) ? d(aktuell[id]) : null;
    return {
      lohngruppe_id: id,
      name: (id && namen.get(id)) || (id ? 'Lohngruppe' : 'Ohne Lohngruppe'),
      personen: mitglieder.map((p) => p.name),
      produktive_stunden: z2(stunden),
      lohnkosten_je_stunde: z2(lohn),
      selbstkosten_je_stunde: z2(selbst),
      verrechnungssatz: z2(vs),
      aktuell: jetzt ? z2(jetzt) : null,
      differenz: jetzt ? z2(jetzt.minus(vs.runde(2))) : null,
      ampel: jetzt ? ampel(jetzt, selbst, vs) : null,
    };
  });
  for (const id of namen.keys()) {
    if (!gruppenIds.includes(id)) hinweise.push(`${namen.get(id)}: niemand in dieser Lohngruppe — kein eigener Satz berechnet.`);
  }

  const mittellohn = fertigungslohn.durch(produktiveSumme);
  const mittelSelbst = mittellohn.plus(gkJeStunde);
  const mittelVs = mittelSelbst.mal(faktor);

  rechenweg.push(
    { schritt: 'Verrechenbare Stunden aller Personen', wert: z2(produktiveSumme) },
    { schritt: 'Lohnkosten Fertigung', wert: z2(fertigungslohn) },
    { schritt: `Mittellohn = ${fertigungslohn.alsText(2)} € ÷ ${produktiveSumme.alsText(2)} h`, wert: z2(mittellohn) },
    ...posten.map((p) => ({ schritt: `Gemeinkosten: ${p.bezeichnung}`, wert: z2(p.betrag) })),
    ...(gkPersonal.istNull() ? [] : [{ schritt: 'Gemeinkosten: Personal Verwaltung (Anteile)', wert: z2(gkPersonal) }]),
    ...abzuege.map((a) => ({ schritt: `− anders gedeckt: ${a.bezeichnung}`, wert: z2(a.betrag) })),
    { schritt: 'Gemeinkosten im Jahr', wert: z2(gkSumme) },
    { schritt: `Gemeinkosten je Stunde = ${gkSumme.alsText(2)} € ÷ ${produktiveSumme.alsText(2)} h`, wert: z2(gkJeStunde) },
    { schritt: 'Selbstkosten je Stunde (Mittellohn)', wert: z2(mittelSelbst) },
    { schritt: `Wagnis & Gewinn ${wg.alsText(2)} %`, wert: z2(mittelVs.minus(mittelSelbst)) },
    { schritt: 'Verrechnungssatz (Mittellohn)', wert: z2(mittelVs) },
  );

  return {
    jahr,
    bundesland,
    personen: personen.map((p) => ({
      id: p.id,
      name: p.name,
      rolle: p.rolle,
      ist_inhaber: p.ist_inhaber,
      lohngruppe_id: p.lohngruppe_id,
      soll_tage: z2(p._st.soll_tage),
      ausfall_tage: z2(p._st.ausfall_tage),
      anwesenheitsstunden: z2(p._st.anwesenheitsstunden),
      produktiv_anteil: p._st.produktiv_anteil.alsZahl(4),
      produktive_stunden: z2(p._st.produktive_stunden),
      kosten: z2(p._kosten),
      kosten_fertigung: z2(p._fertigung),
      kosten_gemein: z2(p._gemein),
      lohnkosten_je_stunde: p._lohnJeStunde ? z2(p._lohnJeStunde) : null,
      rechenweg: p.rechenweg,
    })),
    produktive_stunden: z2(produktiveSumme),
    fertigungslohn: z2(fertigungslohn),
    gemeinkosten: {
      posten: posten.map((p) => ({ bezeichnung: p.bezeichnung, betrag: z2(p.betrag), quelle: p.quelle })),
      personal: z2(gkPersonal),
      abzuege: abzuege.map((a) => ({ bezeichnung: a.bezeichnung, betrag: z2(a.betrag) })),
      summe: z2(gkSumme),
      je_stunde: z2(gkJeStunde),
      zuschlag_auf_lohn_prozent: gkZuschlag ? z2(gkZuschlag) : null,
    },
    wagnis_gewinn_prozent: z2(wg),
    lohngruppen,
    mittel: {
      lohnkosten_je_stunde: z2(mittellohn),
      selbstkosten_je_stunde: z2(mittelSelbst),
      verrechnungssatz: z2(mittelVs),
    },
    // Gegenprobe: so viel Lohnumsatz braucht der Betrieb im Jahr
    gegenprobe: {
      selbstkosten_jahr: z2(fertigungslohn.plus(gkSumme)),
      lohnumsatz_jahr: z2(mittelVs.mal(produktiveSumme)),
    },
    rechenweg,
    hinweise,
    fehler,
  };
}
